import styled, { css } from 'styled-components';
import { BaseProductDTO, isProtocol, ProductDTO } from 'src/models/pim';
import {
  ProductAttributeCode,
  ProductTextValuesAttribute,
} from 'src/models/pim/attributes';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { Heading } from 'src/components/common/Heading';
import { useMemo } from 'react';
import { ProductPageSctSectionItem } from 'src/modules/products/components/ProductPage/ProductPageSctSection/ProductPageSctSectionItem';
import { media } from 'src/styles/designs';
import { head } from 'lodash';
import { Theme } from 'vendor/mods/styled-components';
import { dirRTL, redTheme } from 'src/styles/theme';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { useTitlesDictionary } from 'src/modules/products/hooks/useTitlesDictionary';
import { DictionaryTitlesCode } from 'src/models/pim/dictionaries';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { casePathEmptyString } from 'src/utils/casePathEmptyString';
import { StickyMenuSection } from 'src/components/common/StickyMenu/StickyMenuSection';
import { useTranslate } from 'react-t7e';

const ContentContainer = styled.div`
  display: grid;
  gap: 48px;

  ${media.w.greaterThanOrEqual.desktop} {
    gap: 110px;
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StyledHeading = styled(Heading)`
  width: 100%;
  max-width: 380px;

  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: 32px;
    width: 100%;
  }
`;

const Section = styled(StickyMenuSection)<{ $singular?: boolean }>`
  padding: 32px 0;
  background: ${({ theme }) => theme.palette.background};

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 56px 0;

    ${casePathEmptyString('$singular')(css`
      ${StyledHeading} {
        margin: 30px 230px 48px auto;
        ${dirRTL} {
          margin: 30px auto 48px 300px;
        }
      }

      ${ConstraintContainer} {
        margin-top: 16px;
      }
      ${ContentContainer} {
        grid-template-columns: 1fr;
      }
    `)}
  }
`;

export const ProductPageSctSection = (product: ProductDTO) => {
  const translateFn = useTranslateItem();
  const { _ } = useTranslate();

  const { getProductAttribute, getMultipleProductAttributes } =
    useProductAttributes(product.attributes);

  const [title] = useTitlesDictionary(DictionaryTitlesCode.Sct);

  const items = useMemo(() => {
    if (isProtocol(product)) {
      return product.cross_sell.map((p) => translateFn(p.related_products_id));
    }

    return getMultipleProductAttributes(ProductAttributeCode.SctLinkGroup)
      .map((attributes) => {
        const sctProduct = translateFn(
          head(
            getProductAttribute(
              ProductAttributeCode.SctLinkGroupProduct,
              attributes,
            ),
          )?.products_id ?? ({} as BaseProductDTO),
        );

        return {
          ...sctProduct,
          image:
            head(
              getProductAttribute(
                ProductAttributeCode.SctLinkGroupImage,
                attributes,
              ),
            )?.directus_files_id ?? sctProduct.image,
          description: translateFn(
            head(
              getProductAttribute(
                ProductAttributeCode.SctLinkGroupText,
                attributes,
              ),
            ) ?? ({} as ProductTextValuesAttribute['item']),
          )?.value,
        };
      })
      .filter((p) => !!p.code);
  }, [product, getMultipleProductAttributes, getProductAttribute, translateFn]);

  if (items.length === 0) {
    return null;
  }

  const isSingular = items.length === 1 && !!items[0].image;

  return (
    <Theme theme={redTheme}>
      <Section
        id="sct"
        label={_('Other protocols')}
        index={3}
        threshold={{
          desktop: 0.9,
          mobile: 0.8,
        }}
        $singular={isSingular}
      >
        <ConstraintContainer>
          <StyledHeading>
            {title && <Markdown>{title.value}</Markdown>}
          </StyledHeading>

          <ContentContainer>
            {items.map((item, i) => (
              <ProductPageSctSectionItem
                key={i}
                singular={isSingular}
                {...item}
              />
            ))}
          </ContentContainer>
        </ConstraintContainer>
      </Section>
    </Theme>
  );
};
